/**
 * The three location controls of the register rail: a place (typeahead over
 * /api/geo/suggest), a radius around it while it is a point, and a map area
 * drawn or picked in the area dialog. A map area cancels the other two and
 * choosing a place cancels the area.
 */
import { useEffect, useState } from 'react'
import { IconChevronRight } from '@tabler/icons-react'
import { useGeoSuggest } from '../../api/queries'
import type { GeoSuggestion, PropertyMarkerQuery } from '../../api/types'
import { ActionRow, PickerRow, Popover } from '../../ui/kit'
import { AreaMapDialog } from './AreaMapDialog'
import { DEFAULT_RADIUS_MILES, anchorKindLabel, radiusChoices, toAnchor, type LocationArea, type RegisterLocation } from './location'

const EMPTY_RING: [number, number][] = []

function suggestionLabel(s: GeoSuggestion): string { return s.sublabel ? `${s.label} · ${s.sublabel}` : s.label }

function PlaceRow({ location, onSelect, onClear }: { location: RegisterLocation; onSelect: (s: GeoSuggestion) => void; onClear: () => void }) {
  const anchor = location.locationAnchor
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const suggestions = useGeoSuggest(debounced)
  useEffect(() => { const t = window.setTimeout(() => setDebounced(search.trim()), 250); return () => window.clearTimeout(t) }, [search])
  const options = suggestions.data ?? []
  const empty = debounced.length < 2 ? 'Type at least two characters' : suggestions.isError ? 'Search unavailable' : suggestions.isFetching ? 'Searching…' : 'No locations found'
  return (
    <div className="picker">
      <Popover open={open} onClose={() => setOpen(false)} align="start" matchWidth anchor={
        <button type="button" className={['frow', anchor ? 'is-set' : ''].join(' ')} onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open}>
          <span className="fl">Place</span><span className="fv"><span>{anchor ? anchor.label : 'Any'}</span><IconChevronRight className="ic" aria-hidden="true" /></span>
        </button>
      }>
        <input className="pop-search" placeholder="Postcode, town, road or area" value={search} onChange={e => setSearch(e.target.value)} autoFocus aria-label="Search for a place" />
        <div className="pop-list" role="listbox" aria-label="Places">
          {anchor ? <button type="button" onClick={() => { onClear(); setOpen(false) }}>Any</button> : null}
          {options.map((s, i) => <button type="button" key={`${s.kind}:${s.label}:${i}`} onClick={() => { onSelect(s); setSearch(''); setOpen(false) }}><span className="opt-main"><span className="opt-label">{s.label}</span>{s.sublabel ? <small>{s.sublabel} · {s.kind.replaceAll('_', ' ')}</small> : <small>{s.kind.replaceAll('_', ' ')}</small>}</span></button>)}
          {options.length === 0 ? <span className="t-faint pop-empty">{empty}</span> : null}
        </div>
      </Popover>
      {anchor ? <div className="frow-note"><span title={suggestionLabel({ ...anchor, sublabel: null })}>{anchorKindLabel(anchor)}{anchor.selectionMode === 'boundary' ? ' · boundary' : ''}</span><button type="button" onClick={onClear} aria-label={`Clear location ${anchor.label}`}>Clear</button></div> : null}
    </div>
  )
}

export function LocationRows({ location, markerQuery, onChange }: { location: RegisterLocation; markerQuery: PropertyMarkerQuery; onChange: (patch: Partial<RegisterLocation>) => void }) {
  const { locationAnchor: anchor, locationRadiusMiles: radiusMiles, locationArea: area } = location
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const showRadius = !area && anchor !== null && anchor.selectionMode === 'radius'
  return (
    <>
      {area ? (
        <div className="picker"><div className="frow"><span className="fl">Place</span><span className="fv"><span className="t-faint" title="Cleared while a map area is active">Set by map area</span></span></div></div>
      ) : (
        <PlaceRow location={location} onSelect={s => onChange({ locationAnchor: toAnchor(s), locationRadiusMiles: DEFAULT_RADIUS_MILES, locationArea: null })} onClear={() => onChange({ locationAnchor: null, locationRadiusMiles: DEFAULT_RADIUS_MILES })} />
      )}
      {showRadius && anchor ? (
        <PickerRow label="Radius" value={String(radiusMiles)} defaultValue={String(DEFAULT_RADIUS_MILES)} options={radiusChoices(anchor)} clearable={false} onChange={v => onChange({ locationRadiusMiles: Number(v ?? DEFAULT_RADIUS_MILES) })} />
      ) : null}
      <ActionRow label="Map area" value={area ? area.name : 'Any'} isSet={Boolean(area)} expanded={open} onClick={() => { setMounted(true); setOpen(true) }}
        note={area ? <><span>{area.areaId ? 'saved area' : 'drawn area'}</span><button type="button" onClick={() => onChange({ locationArea: null })} aria-label={`Clear map area ${area.name}`}>Clear</button></> : undefined} />
      {mounted ? (
        <AreaMapDialog
          open={open}
          initialRing={area?.ring ?? EMPTY_RING}
          markerQuery={markerQuery}
          onClose={() => setOpen(false)}
          onApply={(next: LocationArea) => { onChange({ locationArea: next, locationAnchor: null, locationRadiusMiles: DEFAULT_RADIUS_MILES }); setOpen(false) }}
          onDeleted={id => { if (area?.areaId === id) onChange({ locationArea: null }) }}
        />
      ) : null}
    </>
  )
}
